import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ShieldCheck, Mail, Lock, User, Building, AlertCircle, Zap, FileText, Shield } from 'lucide-react';
import { toast } from 'sonner';
import useSEO from '../hooks/useSEO';
import { readPendingDomain } from '../components/DomainStart';

export default function RegisterPage() {
  useSEO('/register');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    company: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    if (formData.password.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }

    setLoading(true);

    try {
      await register(formData.email, formData.password, formData.name, formData.company);
      toast.success('Compte créé avec succès !');
      // Domaine saisi sur la page d'accueil : direction l'audit offert, pré-rempli.
      navigate(readPendingDomain() ? '/audits/new' : '/billing');
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la création du compte');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090b] flex">
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-2 mb-8">
              <ShieldCheck className="w-10 h-10 text-blue-500" />
              <span className="font-mono text-2xl font-bold text-white">CyberGuard</span>
            </Link>
            <h1 className="text-2xl font-bold text-white mb-2 font-mono uppercase tracking-tighter">Créer un compte</h1>
            <p className="text-zinc-400 font-mono text-xs uppercase tracking-widest">Accédez à la plateforme de sécurité IA</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-sm text-red-400 text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="name" className="text-zinc-300">Nom complet</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input
                  id="name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Jean Dupont"
                  className="pl-10 bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-600"
                  required
                  data-testid="register-name-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-zinc-300">Email professionnel</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="vous@entreprise.com"
                  className="pl-10 bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-600"
                  required
                  data-testid="register-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="company" className="text-zinc-300">Entreprise (optionnel)</Label>
              <div className="relative">
                <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input
                  id="company"
                  name="company"
                  type="text"
                  value={formData.company}
                  onChange={handleChange}
                  placeholder="Nom de l'entreprise"
                  className="pl-10 bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-600"
                  data-testid="register-company-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-zinc-300">Mot de passe</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input
                  id="password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  aria-describedby="register-password-help"
                  autoComplete="new-password"
                  className="pl-10 bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-600"
                  required
                  data-testid="register-password-input"
                />
              </div>
              <p id="register-password-help" className="text-xs text-zinc-400">8 caractères minimum.</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-zinc-300">Confirmer le mot de passe</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="••••••••"
                  className="pl-10 bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-600"
                  required
                  data-testid="register-confirm-password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-blue-600 hover:bg-blue-700 font-mono uppercase font-bold rounded-none"
              disabled={loading}
              data-testid="register-submit-btn"
            >
              {loading ? 'Création...' : 'Créer mon compte'}
            </Button>

          </form>

          <p className="mt-8 text-center text-zinc-400 text-sm">
            Déjà un compte ?{' '}
            <Link to="/login" className="text-blue-500 hover:text-blue-400" data-testid="register-login-link">
              Se connecter
            </Link>
          </p>
        </div>
      </div>

      {/* Right side */}
      <div className="hidden lg:flex flex-1 items-center justify-center bg-zinc-900/30 border-l border-zinc-800 p-12">
        <div className="max-w-md space-y-8">
          <div className="space-y-2">
            <div className="text-[10px] font-mono text-blue-400 uppercase tracking-widest">Plateforme professionnelle</div>
            <h2 className="text-3xl font-black text-white font-mono uppercase tracking-tighter leading-none">
              Sécurisez<br /><span className="text-blue-500">votre infrastructure</span>
            </h2>
            <p className="text-zinc-500 font-mono text-xs leading-relaxed">
              Après inscription, choisissez le plan adapté à vos besoins et commencez vos audits immédiatement.
            </p>
          </div>

          <div className="space-y-3">
            {[
              { icon: Shield, color: 'text-blue-400', border: 'border-blue-500/20', label: 'Starter — 29€/mois', sub: '5 audits · SSL, HTTPS, headers · DNS (SPF, DMARC, DKIM)' },
              { icon: Zap, color: 'text-green-400', border: 'border-green-500/20', label: 'Pro — 79€/mois', sub: '30 audits · Rapport IA · Guide de correction' },
              { icon: FileText, color: 'text-purple-400', border: 'border-purple-500/20', label: 'Enterprise — 199€/mois', sub: 'Audits illimités · Export PDF · API · Support prioritaire' },
            ].map(({ icon: Icon, color, border, label, sub }) => (
              <div key={label} className={`flex items-center gap-3 p-4 bg-zinc-900 border ${border} rounded-sm`}>
                <Icon className={`w-5 h-5 ${color} shrink-0`} strokeWidth={1.5} />
                <div>
                  <div className={`text-xs font-mono font-bold uppercase ${color}`}>{label}</div>
                  <div className="text-zinc-500 text-[10px] font-mono">{sub}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-3 gap-3 pt-4 border-t border-zinc-800">
            {[
              { val: '50+', label: 'Checks' },
              { val: '<2min', label: 'Scan' },
              { val: 'IA', label: 'Rapports' },
            ].map(({ val, label }) => (
              <div key={label} className="text-center">
                <div className="text-xl font-black font-mono text-blue-400">{val}</div>
                <div className="text-[9px] text-zinc-600 font-mono uppercase tracking-widest">{label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
